import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { USER_TIERS } from '../services/UserService';
import featureAccess from '../utils/featureAccess';
import PremiumUpgradeModal from './PremiumUpgradeModal';
import SubscriptionStatus from './SubscriptionStatus';

function ProBanner() {
  const { currentUser, updateUserTier, userTier } = useAuth();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Only show purchase options for basic users
  const showPurchaseOptions = userTier === USER_TIERS.BASIC;

  const handleOpenUpgradeModal = () => {
    setIsUpgradeModalOpen(true);
  };

  const handleSubscriptionSuccess = async (subscriptionData) => {
    try {
      // Update user tier to pro
      if (currentUser) {
        await updateUserTier(USER_TIERS.PRO);
      }
      setIsUpgradeModalOpen(false);
    } catch (error) {
      console.error('Error updating user tier:', error);
    }
  };

  // Pro features to highlight
  const proFeatures = [
    featureAccess.getProFeatureDetails('savedWorkouts'),
    featureAccess.getProFeatureDetails('detailedAnalytics'),
    featureAccess.getProFeatureDetails('workoutPrograms'),
    featureAccess.getProFeatureDetails('userProfiles')
  ];

  // Already on pro tier
  if (userTier === USER_TIERS.PRO) {
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
          <div className="flex items-center mb-3 md:mb-0">
            <div className="text-xl mr-2">✨</div>
            <div>
              <h3 className="font-semibold text-indigo-800">PilatesPlanner Pro</h3>
              <p className="text-sm text-indigo-600">Thank you for your support! Enjoy all pro features.</p>
            </div>
          </div>
          <SubscriptionStatus />
        </div>
      </div>
    );
  }

  return (
    <div id="pro" className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg overflow-hidden mb-6">
      <div className="flex items-center p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="text-xl mr-3 text-yellow-500">⭐</div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800">Upgrade to PilatesPlanner Pro</h3>
          <p className="text-sm text-gray-600">
            {userTier === USER_TIERS.BASIC ? 
              'Enhance your Pilates experience with pro features' : 
              'Create an account to save workouts and access pro features'}
          </p>
        </div>
        <button
          className={`p-1 rounded-full transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
          aria-label={expanded ? "Collapse pro details" : "Expand pro details"}
          aria-expanded={expanded}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      
      {expanded && (
        <div className="border-t border-blue-100 p-4 bg-white bg-opacity-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {proFeatures.map((feature, index) => (
              <div key={index} className="flex p-3 bg-white bg-opacity-60 rounded-lg">
                <div className="text-2xl mr-3">{feature.icon}</div>
                <div>
                  <h4 className="font-medium text-gray-800">{feature.name}</h4>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                  {feature.basicLimit !== undefined && (
                    <p className="text-xs text-blue-600 mt-1">
                      Basic tier: {feature.basicLimit} {feature.basicLimit === 1 ? 'only' : 'max'}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4">
            {showPurchaseOptions ? (
              <>
                <button
                  onClick={handleOpenUpgradeModal}
                  className="flex items-center justify-center px-6 py-2 rounded-md text-white font-medium
                    bg-blue-600 hover:bg-blue-700 shadow-sm transition-colors w-full sm:w-auto"
                >
                  Upgrade to Pro
                </button>
                <span className="text-xs text-gray-500">Starting at $1.99/month. No commitment, cancel anytime</span>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                Create an account to unlock basic features and upgrade to pro for unlimited access.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
}

export default ProBanner;